import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Pagination,
  Alert,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { groupService } from '../../services/group.service';
import { userService } from '../../services/user.service';
import { PublicUser } from '../../types/user.types';
import { MemberRow } from './MemberRow';
import { formatHandle } from '../ui/uiHelpers';
import { formatDate } from '../../i18n/locale-format';

interface GroupMember {
  _id: string;
  userId: string;
  role: 'admin' | 'member';
  joinedAt: string;
}

interface GroupMembersProps {
  groupId: string;
  userRole: 'admin' | 'member' | null;
  currentUserId: string;
}

export const GroupMembers = ({ groupId, userRole, currentUserId }: GroupMembersProps) => {
  const { t } = useTranslation('groups');
  const [members, setMembers] = useState<GroupMember[]>([]);
  const [memberUsers, setMemberUsers] = useState<PublicUser[]>([]); // User details for members
  const [joinRequests, setJoinRequests] = useState<string[]>([]);
  const [joinRequestUsers, setJoinRequestUsers] = useState<PublicUser[]>([]); // User details for join requests
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState(''); // For controlled input
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);

  // Dialog states
  const [roleDialog, setRoleDialog] = useState<{ open: boolean; member: GroupMember | null }>({
    open: false,
    member: null
  });
  const [addMemberDialog, setAddMemberDialog] = useState(false);
  const [newMemberForm, setNewMemberForm] = useState({ user: null as PublicUser | null, role: 'member' as 'admin' | 'member' });
  const [userSearchResults, setUserSearchResults] = useState<PublicUser[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);

  const isAdmin = userRole === 'admin';
  const limit = 10;

  useEffect(() => {
    loadMembers();
    if (isAdmin) {
      loadJoinRequests();
    }
  }, [groupId, page, searchQuery]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMembers = async () => {
    try {
      setLoading(true);
      const response = await groupService.getGroupMembers(groupId, page, limit, searchQuery);
      const membersData = response.data.members;
      setMembers(membersData);
      setTotalPages(Math.ceil(response.data.total / limit) || 1);

      // Fetch user details for each member
      const userPromises = membersData.map((member: GroupMember) =>
        userService.getUserById(member.userId).catch(err => {
          console.error(`Failed to load user ${member.userId}:`, err);
          return null;
        })
      );

      const userResponses = await Promise.all(userPromises);
      const users = userResponses
        .filter(response => response !== null)
        .map(response => response!.data);

      setMemberUsers(users);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || t('members.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const loadJoinRequests = async () => {
    try {
      const response = await groupService.getPendingJoinRequests(groupId);
      const userIds = response.data;
      setJoinRequests(userIds);
      
      // Fetch user details for each join request
      const userPromises = userIds.map((userId: string) => 
        userService.getUserById(userId).catch(err => {
          console.error(`Failed to load user ${userId}:`, err);
          return null;
        })
      );
      
      const userResponses = await Promise.all(userPromises);
      const users = userResponses
        .filter(response => response !== null)
        .map(response => response!.data);
      
      setJoinRequestUsers(users);
    } catch (err: any) {
      console.error('Failed to load join requests:', err);
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (!window.confirm(t('members.removeConfirm'))) return;
    
    try {
      await groupService.removeGroupMember(groupId, userId);
      loadMembers();
    } catch (err: any) {
      setError(err.response?.data?.message || t('members.errors.removeFailed'));
    }
  };

  const handleUpdateRole = async (userId: string, newRole: 'admin' | 'member') => {
    try {
      await groupService.updateMemberRole(groupId, userId, newRole);
      loadMembers();
      setRoleDialog({ open: false, member: null });
    } catch (err: any) {
      setError(err.response?.data?.message || t('members.errors.updateRoleFailed'));
    }
  };

  const handleAddMember = async () => {
    if (!newMemberForm.user) return;
    
    try {
      await groupService.addGroupMember(groupId, {
        userId: newMemberForm.user._id,
        role: newMemberForm.role
      });
      loadMembers();
      setAddMemberDialog(false);
      setNewMemberForm({ user: null, role: 'member' });
    } catch (err: any) {
      setError(err.response?.data?.message || t('members.errors.addFailed'));
    }
  };

  const handleSearchChange = (value: string) => {
    // Update input value immediately for responsive UI
    setSearchInput(value);

    // Clear any existing timeout
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    // Set new timeout for debouncing (1 second delay)
    const timeout = setTimeout(() => {
      setSearchQuery(value);
      setPage(1); // Reset to first page when searching
    }, 1000); // 1 second debounce

    setSearchTimeout(timeout);
  };

  const searchUsers = async (query: string) => {
    if (!query.trim()) {
      setUserSearchResults([]);
      return;
    }

    try {
      setUserSearchLoading(true);
      const response = await userService.searchUsers(query);

      // Filter out users who are already members of the group
      const existingMemberIds = members.map(member => member.userId);
      const filteredUsers = response.data
        .filter(user => !existingMemberIds.includes(user._id))
        .slice(0, 10); // Limit to top 10 results

      setUserSearchResults(filteredUsers);
    } catch (err: any) {
      console.error('Failed to search users:', err);
      setUserSearchResults([]);
    } finally {
      setUserSearchLoading(false);
    }
  };

  const handleApproveRequest = async (userId: string) => {
    try {
      await groupService.approveJoinRequest(groupId, userId);
      loadJoinRequests();
      loadMembers();
    } catch (err: any) {
      setError(err.response?.data?.message || t('members.errors.approveFailed'));
    }
  };

  const handleRejectRequest = async (userId: string) => {
    try {
      await groupService.rejectJoinRequest(groupId, userId);
      loadJoinRequests();
    } catch (err: any) {
      setError(err.response?.data?.message || t('members.errors.rejectFailed'));
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {error && (
        <Alert severity="error" sx={{ borderRadius: '8px' }}>
          {error}
        </Alert>
      )}

      {/* Join requests (admin only) */}
      {isAdmin && joinRequests.length > 0 && (
        <Box sx={{ border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1.5, fontSize: 14, fontWeight: 600, backgroundColor: 'var(--warn-bg)', color: 'var(--warn)' }}>
            {t('members.pendingTitle', { count: joinRequests.length })}
          </Box>
          {joinRequestUsers.map((user) => (
            <MemberRow
              key={user._id}
              name={user.displayName}
              secondary={[formatHandle(user.username), t('members.pendingApproval')].filter(Boolean).join(' · ')}
              actions={
                <>
                  <Button size="small" variant="contained" onClick={() => handleApproveRequest(user._id)}>
                    {t('members.approve')}
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => handleRejectRequest(user._id)}>
                    {t('members.reject')}
                  </Button>
                </>
              }
            />
          ))}
        </Box>
      )}

      {/* Members */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ fontSize: 15, fontWeight: 600 }}>{t('members.title', { count: members.length })}</Box>
        {isAdmin && (
          <Button startIcon={<PersonAddIcon />} variant="outlined" onClick={() => setAddMemberDialog(true)}>
            {t('members.addMember')}
          </Button>
        )}
      </Box>

      <TextField
        fullWidth
        placeholder={t('members.searchPlaceholder')}
        size="small"
        value={searchInput}
        onChange={(e) => handleSearchChange(e.target.value)}
      />

      <Box sx={{ border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress size={28} />
          </Box>
        ) : members.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center', fontSize: 14, color: 'var(--muted)' }}>{t('members.noMembers')}</Box>
        ) : (
          members.map((member) => {
            const user = memberUsers.find(u => u._id === member.userId);
            return (
              <MemberRow
                key={member._id}
                name={user ? user.displayName : t('members.unknownUser', { id: member.userId })}
                secondary={[formatHandle(user?.username), t('members.joined', { date: formatDate(member.joinedAt) })].filter(Boolean).join(' · ')}
                role={member.role}
                actions={
                  isAdmin && member.userId !== currentUserId ? (
                    <>
                      <IconButton size="small" aria-label={t('members.changeRoleAria')} onClick={() => setRoleDialog({ open: true, member })}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" aria-label={t('members.removeAria')} sx={{ color: 'var(--danger)' }} onClick={() => handleRemoveMember(member.userId)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </>
                  ) : undefined
                }
              />
            );
          })
        )}
      </Box>

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center">
          <Pagination count={totalPages} page={page} onChange={(_, newPage) => setPage(newPage)} />
        </Box>
      )}

      {/* Role Update Dialog */}
      <Dialog open={roleDialog.open} onClose={() => setRoleDialog({ open: false, member: null })}>
        <DialogTitle>{t('members.roleDialogTitle')}</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {roleDialog.member && (() => {
              const user = memberUsers.find(u => u._id === roleDialog.member!.userId);
              const handle = user ? formatHandle(user.username) : undefined;
              const name = user
                ? handle ? `${user.displayName} (${handle})` : user.displayName
                : t('members.unknownUser', { id: roleDialog.member.userId });
              return t('members.updateRoleFor', { name });
            })()}
          </Typography>
          <TextField
            select
            fullWidth
            label={t('members.role')}
            value={roleDialog.member?.role || 'member'}
            onChange={(e) => {
              if (roleDialog.member) {
                setRoleDialog({
                  ...roleDialog,
                  member: { ...roleDialog.member, role: e.target.value as 'admin' | 'member' }
                });
              }
            }}
            sx={{ mt: 2 }}
          >
            <MenuItem value="member">{t('roles.member')}</MenuItem>
            <MenuItem value="admin">{t('roles.admin')}</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleDialog({ open: false, member: null })}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={() => roleDialog.member && handleUpdateRole(roleDialog.member.userId, roleDialog.member.role)}
            variant="contained"
          >
            {t('common:actions.update')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Member Dialog */}
      <Dialog open={addMemberDialog} onClose={() => setAddMemberDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('members.addDialogTitle')}</DialogTitle>
        <DialogContent>
          <Autocomplete
            fullWidth
            options={userSearchResults}
            getOptionLabel={(user) => [user.displayName, formatHandle(user.username)].filter(Boolean).join(' ')}
            value={newMemberForm.user}
            onChange={(_, newValue) => setNewMemberForm({ ...newMemberForm, user: newValue })}
            onInputChange={(_, newInputValue) => {
              searchUsers(newInputValue);
            }}
            loading={userSearchLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t('members.searchUsers')}
                placeholder={t('members.searchUsersPlaceholder')}
                sx={{ mt: 2, mb: 2 }}
                slotProps={{
                  input: {
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {userSearchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps?.endAdornment}
                      </>
                    ),
                  }
                }}
              />
            )}
            renderOption={(props, user) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {user.displayName}
                  </Typography>
                  {formatHandle(user.username) && (
                    <Typography variant="caption" color="text.secondary">
                      {formatHandle(user.username)}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            noOptionsText={userSearchLoading ? t('members.searching') : t('members.noUsers')}
          />
          <TextField
            select
            fullWidth
            label={t('members.role')}
            value={newMemberForm.role}
            onChange={(e) => setNewMemberForm({ ...newMemberForm, role: e.target.value as 'admin' | 'member' })}
          >
            <MenuItem value="member">{t('roles.member')}</MenuItem>
            <MenuItem value="admin">{t('roles.admin')}</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddMemberDialog(false)}>
            {t('common:actions.cancel')}
          </Button>
          <Button
            onClick={handleAddMember}
            variant="contained"
            disabled={!newMemberForm.user}
          >
            {t('members.addMemberSubmit')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};